/**
 * 会议界面现代化改造 — 布局与尺寸常量（不含颜色字面量，颜色走主题 token）。
 */
/** 视频块 / 缩略图圆角（px）。 */
export const VIDEO_TILE_BORDER_RADIUS = 12;
/** 工具栏功能按钮圆角（px）。 */
export const TOOLBAR_BUTTON_BORDER_RADIUS = 8;
/** 发言指示器胶囊圆角（px）。 */
export const SPEAKING_INDICATOR_BORDER_RADIUS = 20;
/** 姓名标签 pill 圆角（px）。 */
export const DISPLAY_NAME_BADGE_BORDER_RADIUS = 16;
/** 全宽底栏高度（px，不含 safe-area）。 */
export const TOOLBAR_FULL_WIDTH_HEIGHT = 72;
/** 顶栏高度（px）。 */
export const CONFERENCE_HEADER_HEIGHT = 48;
/** 竖条人员列表与底栏之间的间距（px）。 */
export const FILMSTRIP_TOOLBAR_GAP = 8;
/** 工具栏按钮图标尺寸（px）。 */
export const TOOLBAR_ICON_SIZE = 24;
/** 工具栏按钮标签字号（rem）。 */
export const TOOLBAR_LABEL_FONT_SIZE = '0.625rem';
/** 大画面无视频时圆形头像直径占视口高度比例。 */
export const STAGE_AVATAR_SIZE_RATIO = 0.28;
/** 大画面圆形头像直径上限（px），与 CSS min(28vh, 280px) 一致。 */
export const STAGE_AVATAR_MAX_PX = 280;
/** 底栏左右区不再强制大 min-width，避免窄窗横向撑破导致挂断贴边/裁切。 */
export const TOOLBAR_SIDE_SECTION_MIN_WIDTH = 0;
/** 底栏左右内边距（px），与 toolbox-content-items padding 一致。 */
export const TOOLBAR_HORIZONTAL_PADDING = 24;
/**
 * 挂断菜单相对视口右缘的间距（px）。
 * 与底栏右内边距对齐，使弹出层右缘贴齐画面最右侧。
 */
export const HANGUP_MENU_VIEWPORT_RIGHT_OFFSET = TOOLBAR_HORIZONTAL_PADDING;
/**
 * 挂断菜单最大宽度（px）。
 */
export const HANGUP_MENU_MAX_WIDTH = 280;
/**
 * 挂断 Popover 定位：底栏大按钮上方展开，右缘对齐触发器（会议区最右，不贴侧栏/整页最右）。
 */
export const HANGUP_POPOVER_POSITION = 'top-trigger-end';
/** Filmstrip 缩略图间距（px）。 */
export const FILMSTRIP_TILE_GAP = 8;
/** 工具栏 labeled 模式 customClass。 */
export const TOOLBAR_LABELED_BUTTON_CLASS = 'toolbar-labeled-button';
/** 非主持人底栏「离开会议」挂断按钮 CSS class。 */
export const TOOLBAR_HANGUP_BUTTON_CLASS = 'hangup-button';
/** 主持人底栏「结束会议」菜单触发按钮 CSS class。 */
export const TOOLBAR_HANGUP_MENU_BUTTON_CLASS = 'hangup-menu-button';
/** labeled 挂断外层修饰 class（图标区 + 红色标签）。 */
export const TOOLBAR_LABELED_HANGUP_CLASS = 'toolbar-labeled-hangup';
/**
 * 是否为底栏挂断类按钮（离开会议 / 结束会议菜单）。
 *
 * @param {string|undefined} customClass - ToolboxItem 的 customClass。
 * @returns {boolean}
 */
export function isToolbarHangupCustomClass(customClass) {
    if (!customClass) {
        return false;
    }
    const classes = customClass.split(/\s+/);
    return classes.includes(TOOLBAR_HANGUP_BUTTON_CLASS)
        || classes.includes(TOOLBAR_HANGUP_MENU_BUTTON_CLASS);
}
/**
 * 设备选择小箭头（音频/视频旁 caret）尺寸（px）。
 * 对齐腾讯会议式细箭头，远小于主图标。
 */
export const TOOLBAR_SETTINGS_CARET_SIZE = 10;
/**
 * 设备选择小箭头相对 labeled 图标区的 top 偏移（px）。
 */
export const TOOLBAR_SETTINGS_CARET_TOP = 0;
/**
 * 设备选择小箭头相对 labeled 图标区的 right 偏移（px）。
 */
export const TOOLBAR_SETTINGS_CARET_RIGHT = 2;
/** 聊天小窗最近消息预览条数上限。 */
export const QUICK_CHAT_PREVIEW_MAX_COUNT = 10;
/** 聊天小窗预览正文截断长度（字符）。 */
export const QUICK_CHAT_PREVIEW_TEXT_MAX_LENGTH = 40;
/**
 * 聊天小窗预览气泡自动消失时长（毫秒）。
 * 消息时间戳超过此时长后不再展示。
 */
export const QUICK_CHAT_PREVIEW_TTL_MS = 10_000;
/**
 * 聊天小窗预览气泡淡出动画时长（毫秒）。
 * TTL 到期后先播放向上淡出，再从 DOM 移除。
 */
export const QUICK_CHAT_PREVIEW_FADE_OUT_MS = 600;
/**
 * 聊天小窗预览气泡出现时向上滑入时长（毫秒）。
 */
export const QUICK_CHAT_PREVIEW_SLIDE_IN_MS = 320;
/**
 * 聊天气泡滑入/淡出位移（px）：正值表示从下方滑入，淡出时继续上移。
 */
export const QUICK_CHAT_PREVIEW_SLIDE_DISTANCE_PX = 14;
/** 聊天小窗输入条高度（px）。 */
export const QUICK_CHAT_INPUT_HEIGHT = 36;
/** 聊天小窗最小宽度（px）。 */
export const QUICK_CHAT_MIN_WIDTH = 200;
/** 聊天小窗最大宽度（px）。 */
export const QUICK_CHAT_MAX_WIDTH = 280;
/** 聊天小窗预览气泡最大宽度（px）。 */
export const QUICK_CHAT_PREVIEW_MAX_WIDTH = 260;
/** 聊天小窗预览与输入条间距（px）。 */
export const QUICK_CHAT_PREVIEW_GAP = 8;
/** 聊天小窗胶囊圆角（px）。 */
export const QUICK_CHAT_PILL_BORDER_RADIUS = 20;
/** 聊天小窗预览气泡圆角（px）。 */
export const QUICK_CHAT_PREVIEW_BORDER_RADIUS = 8;
/**
 * 聊天小窗预览气泡相对底栏的层叠顺序。
 * 需高于输入条，保证气泡悬浮在底栏内容之上、不被裁切后遮挡。
 */
export const QUICK_CHAT_PREVIEW_Z_INDEX = 3;
/**
 * 手机 Web 端屏幕共享聚焦布局：共享者小窗宽度（px）。
 */
export const MOBILE_SCREENSHARE_PIP_WIDTH = 112;
/**
 * 手机 Web 端屏幕共享聚焦布局：共享者小窗高度（px）。
 */
export const MOBILE_SCREENSHARE_PIP_HEIGHT = 150;
/**
 * 手机 Web 端屏幕共享聚焦布局：共享者小窗圆角（px）。
 */
export const MOBILE_SCREENSHARE_PIP_BORDER_RADIUS = 8;
/**
 * 手机 Web 端屏幕共享聚焦布局：共享者小窗相对内容区右上角的边距（px）。
 */
export const MOBILE_SCREENSHARE_PIP_OFFSET = 12;
/**
 * 手机 Web 端屏幕共享聚焦布局：共享者小窗内头像直径（px）。
 */
export const MOBILE_SCREENSHARE_PIP_AVATAR_SIZE = 56;
/**
 * 手机 Web 端屏幕共享聚焦布局：右下角横竖屏切换按钮直径（px）。
 */
export const MOBILE_SCREENSHARE_ROTATE_BUTTON_SIZE = 44;
/**
 * 手机 Web 端屏幕共享聚焦布局：浮动控件相对底栏的上间距（px）。
 */
export const MOBILE_SCREENSHARE_FLOAT_BOTTOM_GAP = 12;
/**
 * 手机 Web 端屏幕共享聚焦布局：浮动控件相对屏幕右缘间距（px）。
 */
export const MOBILE_SCREENSHARE_FLOAT_RIGHT_GAP = 12;
/**
 * 手机 Web 端屏幕共享聚焦布局：浮动层 z-index（低于 toolbox，高于 large video）。
 */
export const MOBILE_SCREENSHARE_OVERLAY_Z_INDEX = 220;
/**
 * body class：手机共享聚焦模式（隐藏侧栏布局）。
 */
export const MOBILE_SCREENSHARE_FOCUS_BODY_CLASS = 'mobile-screenshare-focus';
/**
 * body class：CSS 模拟横屏（Screen Orientation API 不可用时的回退）。
 */
export const MOBILE_SCREENSHARE_LANDSCAPE_BODY_CLASS = 'mobile-screenshare-landscape';
/** 手机底栏悬浮入口相对底栏的上间距（px）. */
export const MOBILE_TOOLBOX_SHORTCUTS_GAP = 10;
/** 手机底栏悬浮入口左右内边距（px）. */
export const MOBILE_TOOLBOX_SHORTCUTS_INSET = 12;
/** 手机底栏聊天胶囊高度（px）. */
export const MOBILE_CHAT_PILL_HEIGHT = 36;
/** 手机底栏聊天胶囊最大宽度（px）. */
export const MOBILE_CHAT_PILL_MAX_WIDTH = 280;
/** 手机底栏聊天胶囊未读绿点直径（px）. */
export const MOBILE_CHAT_PILL_UNREAD_DOT_SIZE = 8;
/** 手机底栏 AI 纪要圆形按钮直径（px）. */
export const MOBILE_AI_NOTES_FAB_SIZE = 40;
/** 手机底栏 AI 纪要角标直径（px）. */
export const MOBILE_AI_NOTES_BADGE_SIZE = 14;
